/**
 * ================================================================================================
 *  THE CHARGES OF EVERY CUSTOMER — A SECTION OF ITS OWN, UNDER BILLING
 * ================================================================================================
 *
 * The Owner, 2026-11-02, looking at it on the health screen:
 *
 *     *"צריך להיות בקטגוריה נפרדת בבילינג ולא בבריאות המערכת — זה לא קשור לבריאות מערכת,
 *     זה צריך להיות בנפרד."*
 *
 * He is right, and the distinction is not cosmetic. **Health answers *"is this estate wired up"*;
 * this answers *"and what has it done with the money"*.** They have different capabilities,
 * different reads, different readers and different urgencies — a failed charge is a customer to
 * telephone, not a subsystem to restart — and putting the second inside the first meant the one
 * screen an operator opens when something is broken was also the only place to look for a
 * customer's invoice number.
 *
 * WHAT STAYED ON THE HEALTH SCREEN is the one clearing fact that IS health: whether this estate
 * has a provider configured at all. `none` there means self-signup cannot complete, which is a
 * subsystem answer; everything about what was charged, to whom, and which document came out is
 * here.
 *
 * It is a MODULE of its own rather than a function inside the billing screen, because it is the
 * whole of one read and the billing screen owns three others.
 */

import type { JSX } from 'react';

import { DataTable } from '../components/DataTable';
import { FactList } from '../components/FactList';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import type { ClearingAttemptView, ClearingOverviewView } from '../api/contract';


/**
 * THE CLEARING CONNECTION'S OWN PANEL. *"החיובים, הכשלים והמסמכים בפועל."*
 *
 * ── THE UNRESOLVED LIST IS FIRST, BECAUSE IT IS THE ONE THAT NEEDS A PERSON ───────────────
 *
 * There should be none. A row here is a charge whose outcome at the provider is UNKNOWN — a
 * timeout, a dropped connection — with the idempotency key already spent, so the period is
 * neither collected nor uncollected until somebody reads that key back from Cardcom. Nothing in
 * this product resolves one automatically, because an unattended process that decides to charge or
 * to refund is not a thing anybody has approved.
 *
 * A DECLINE IS NOT IN THIS LIST. It is a commercial fact and the dunning policy handles it on the
 * Owner's own cadence; mixing the two would bury the four rows that matter under a hundred that do
 * not.
 */
export function ClearingPanel({ overview }: { readonly overview: ClearingOverviewView }): JSX.Element {
  const unchecked = overview.instruments.find((row) => row.provider === 'sandbox')?.held ?? 0;
  return (
    <section className="gp-panel">
      <h2 className="breakdown__title">סליקה — כרטיסים וחיובים</h2>

      {/*
        THE CARDS THIS ESTATE HOLDS, SPLIT BY WHO CHECKED THEM. A single total would hide the one
        number an operator has to act on: offices whose card no provider ever saw.
      */}
      <FactList
        facts={[
          {
            labelHe: 'כרטיסים פעילים',
            value:
              overview.instruments.length === 0 ? null : (
                <span dir="ltr">
                  {overview.instruments
                    .map((row) => `${row.provider}: ${String(row.held)}`)
                    .join('  ·  ')}
                </span>
              ),
            absentHe: 'אין כרטיסים שמורים באסטט הזאת.',
          },
        ]}
      />

      {unchecked > 0 ? (
        <StateBlock
          titleHe={`${String(unchecked)} ארגונים מחזיקים כרטיס שאף חברת סליקה לא בדקה`}
          bodyHe="הכרטיסים האלו נרשמו במסלול בדיקה. כל ניסיון חיוב שלהם ייכשל, ויש לבקש מהם כרטיס אמיתי לפני החיוב הראשון."
          tone="unavailable"
        />
      ) : null}

      {overview.unresolved.length > 0 ? (
        <>
          <StateBlock
            titleHe={`${String(overview.unresolved.length)} חיובים ללא תוצאה ידועה`}
            bodyHe="הקריאה לחברת הסליקה לא הושלמה, כלומר ייתכן שהכסף זז וייתכן שלא. המזהה הייחודי של החיוב כבר נוצל, לכן לא ייתכן חיוב כפול על התקופה הזאת. יש לקרוא את המזהה מול הספק ולהכריע."
            tone="error"
          />
          <AttemptTable rows={overview.unresolved} captionHe="חיובים ללא תוצאה ידועה" />
        </>
      ) : null}

      <h3 className="breakdown__title">חיובים אחרונים</h3>
      <AttemptTable rows={overview.recent} captionHe="ניסיונות החיוב האחרונים" />
    </section>
  );
}

/** One table for both lists, because they are the same rows asking two different questions. */
function AttemptTable({
  rows,
  captionHe,
}: {
  readonly rows: readonly ClearingAttemptView[];
  /*
   * THE CAPTION IS THE CALLER'S. Both lists rendered `ניסיונות החיוב` and the page carried the
   * same caption twice — which a screen reader announces as two identical tables, and which told
   * a sighted reader nothing about which was which either. Found by rendering it.
   */
  readonly captionHe: string;
}): JSX.Element {
  return (
    <DataTable
      captionHe={captionHe}
      emptyStateHe="טרם בוצע חיוב באסטט הזאת."
      rows={rows}
      rowKey={(row) => row.chargeAttemptUid}
      columns={[
        {
          /*
           * ── WHOSE CHARGE IT IS, AND IT COMES FIRST. **Owner, 2026-11-02.** ──────────────────
           *
           * *"כמנהל פלטפורמה אני צריך לראות את כל החיובים של כל הלקוחות — מי נכשל, מי עבר."*
           * The table listed a period, an amount, a state and a provider transaction id, and
           * every row of it was about an office it did not name. An operator reading it could
           * tell that something failed and not who to call.
           *
           * FIRST because it is the column the question starts from. A reader scanning for a
           * customer should not have to cross three columns to find one.
           */
          key: 'customer',
          headerHe: 'לקוח',
          render: (row) => row.tenantName,
        },
        {
          key: 'period',
          headerHe: 'תקופה',
          render: (row) => <span dir="ltr">{`${row.periodStart} – ${row.periodEnd}`}</span>,
        },
        {
          key: 'amount',
          headerHe: 'סכום',
          /* The server's decimal string and the book's code. A price through a number is rounded. */
          render: (row) => <span dir="ltr">{`${row.amountTotal} ${row.currencyCode}`}</span>,
        },
        {
          key: 'state',
          headerHe: 'מצב',
          render: (row) => (
            <>
              <StatusChip
                labelHe={CHARGE_STATE_HE[row.state] ?? row.state}
                /* `muted` for both unsettled states; the WORD carries the difference. 09 S9.5 A3. */
                tone={row.state === 'charged' ? 'ok' : row.state === 'declined' ? 'error' : 'muted'}
              />
              {row.alreadyExisted ? (
                <p className="figure__note">
                  החיוב נמצא קיים אצל הספק ולא בוצע מחדש.
                </p>
              ) : null}
              {row.failureReason === null ? null : (
                <p className="figure__note">{row.failureReason}</p>
              )}
            </>
          ),
        },
        {
          /*
           * ── THE DOCUMENT, AND THE TWO STATES THAT LEFT THE CUSTOMER'S OWN TAB ──────────────
           *
           * *"משתמש הקצה לא צריך לראות טרם הופק או הפקה נכשלה — אלו דברים שמעניינים אותי כמנהל
           * פלטפורמה ולא כמשתמש קצה."* They were taken off the office's screen the same day, and
           * this is where they went: a charge that moved money and produced no document is an
           * incident in OUR estate, and it has to be legible to the person who can act on it.
           *
           * THE NUMBER WHEN THERE IS ONE, THE STATE WHEN THERE IS NOT. A number is the thing an
           * operator reconciles against the provider; a state is the thing they act on. Showing
           * both on every row would put a chip reading *"הופק"* beside the number that proves it.
           */
          key: 'document',
          headerHe: 'מסמך',
          render: (row) =>
            row.documentNumber === null ? (
              <StatusChip
                labelHe={DOCUMENT_STATUS_HE[row.documentStatus] ?? row.documentStatus}
                tone={row.documentStatus === 'provider_failed' ? 'error' : 'muted'}
              />
            ) : (
              <span dir="ltr">{row.documentNumber}</span>
            ),
        },
        {
          key: 'transaction',
          headerHe: 'מספר עסקה אצל הספק',
          /* A STRING, not a number: int64 through a JS number is an id nobody can reconcile. */
          render: (row) => <span dir="ltr">{row.providerTransactionId ?? '—'}</span>,
        },
      ]}
    />
  );
}

/**
 * `CK_billing_document_status`, in the operator's words. **2026-11-02.**
 *
 * The same four the customer's invoices tab used to render, moved rather than deleted: the office
 * no longer sees the two that are about our relationship with the provider, and the person who can
 * do something about them does.
 */
const DOCUMENT_STATUS_HE: Readonly<Record<string, string>> = {
  issued: 'הופק',
  pending_issue: 'טרם הופק',
  provider_failed: 'הפקה נכשלה אצל הספק',
  void: 'בוטל',
};

/** The four states the table labels, keyed off the server's closed vocabulary. */
const CHARGE_STATE_HE: Readonly<Record<string, string>> = {
  in_flight: 'בביצוע',
  charged: 'חויב',
  declined: 'נדחה',
  unknown: 'תוצאה לא ידועה — דורש בדיקה',
};
